import React, { useMemo, useState } from "react";
import { WebsiteBuilderToolbarMenuView } from "../../../WebsiteBuilderToolbarMenuView";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useTranslation } from "react-i18next";
import { TWebsiteBuilderWebsiteVersionPage } from "redux/reducers/models/websiteBuilder";
import Input from "components/form/input/Input";
import { cloneDeep } from "lodash";
import { ButtonNew as Button } from "components/buttonNew";
import "./renamePageView.scss";
import { websiteBuilderRenamePage } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { websiteBuilderSlugify } from "pages/secure/facility/websiteBuilder/editor/utility/websiteBuilderSlugify";

interface IRenamePageViewProps {
  page: TWebsiteBuilderWebsiteVersionPage;
  disabled: boolean;
  goBackToParentView: () => void;
}

export function RenamePageView(props: IRenamePageViewProps) {
  const { page, disabled, goBackToParentView } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const pages = useAppSelector(store => store.websiteBuilderStore.editor.pages);

  const [updatedPage, setUpdatedPage] = useState<TWebsiteBuilderWebsiteVersionPage>(cloneDeep(page));
  
  const updatedTitleValid = useMemo(() => {
    return updatedPage.title?.length > 0 && !pages.some(p => p.id !== updatedPage.id && p.title === updatedPage.title);
  }, [updatedPage, pages]);

  const updatedSlugValid = useMemo(() => {
    return updatedPage.slug?.length > 0 && !pages.some(p => p.id !== updatedPage.id && p.slug === updatedPage.slug);
  }, [updatedPage, pages]);

  const changesExist = page.title !== updatedPage.title || page.slug !== updatedPage.slug;

  const renamePageEnabled =
    !disabled &&
    changesExist &&
    updatedTitleValid &&
    updatedSlugValid;
  
  function updateTitle(title: string) {    
    setUpdatedPage(prev => ({
      ...prev,
      title,
      slug: websiteBuilderSlugify(title),
    }));
  }

  function renderRenamePageUpdateSection() {
    return (
      <div className="website-builder-rename-page-update-section">
        <Input
          label={t("Page name")}
          value={updatedPage.title}
          error={!updatedTitleValid || !updatedSlugValid}
          onChange={e => updateTitle(e.target.value)}
        />
        <div className="rename-page-update-section-domain-preview">{`${t("Page URL")}: /${updatedPage.is_home ? "" : updatedPage.slug}`}</div>
      </div>
    );
  }

  function renamePage() {
    void dispatch(websiteBuilderRenamePage(
      updatedPage.id,
      updatedPage.title,
      updatedPage.slug,
      goBackToParentView,
      t,
    ));
  }

  function renderRenamePageSaveSection() {
    return (
      <div className="website-builder-rename-page-save-section">
        <Button
          type="secondary"
          className="rename-page-action"
          onClick={goBackToParentView}
        >
          {t("Cancel")}
        </Button>
        <Button
          type="primary"
          className="rename-page-action"
          onClick={renamePage}
          disabled={!renamePageEnabled}
        >
          {t("Save")}
        </Button>
      </div>
    );
  }

  return (
    <WebsiteBuilderToolbarMenuView
      title={t("Rename page")}
      goBackToParentView={goBackToParentView}
      footer={renderRenamePageSaveSection()}
      sections={[
        {
          content: renderRenamePageUpdateSection(),
        },
      ]}
    />
  );
}